import {
  AcceptedRunIntentSchema, PlayableRunStateSchema, RunIntentSchema,
  type AcceptedRunIntent, type PlayableRunState, type PlayableRunStateV1, type PlayableRunStateV2, type RunIntent,
} from '@dissidium/contracts';
import {
  activeScene, assertPlayableTransition, canonical, closePlayableLife, createPlayableRun, hash,
  nextPlayableLife, playableHash, reconcilePlayableState, reducePlayableIntent, stripStoredLedger, validatePlayableState,
  validatePlayableStateV1, validatePlayableStateV2,
  type FreshLifeOptions, type LifeInitializer, type LifeOptions,
} from '@dissidium/sim';
import { migrateLedgerDedupe, migratePlayableSave, SaveVersionError } from './migrations/index.ts';
import { SaveCorruptError } from './errors.ts';
import { DurableRunStore, nextTask, type DurableStoreOptions, type LeaseWait, type RollingModel } from './indexeddb/store.ts';
import { IndexedDbDriver, databaseName } from './indexeddb/idb-driver.ts';
import type { StorageDriver } from './indexeddb/driver.ts';

const migrateV1 = (state: Record<string, unknown>): Record<string, unknown> => ({ ...state, version: 2 });

/** Version 3 persists without the duplicated tactical ledger. Versions 1 and 2 are
 * upgraded after their journal is replayed; v2 verifies ledger equality first. */
export function readPlayableSave(input: unknown): PlayableRunState {
  return migratePlayableSave(input, 3, { 1: migrateV1, 2: migrateLedgerDedupe }, validatePlayableState);
}

type StoredPlayableRunState = PlayableRunState | PlayableRunStateV2 | PlayableRunStateV1;
function readStoredPlayableSave(input: unknown): StoredPlayableRunState {
  if (!input || typeof input !== 'object' || !('format' in input) || input.format !== 'playable-run') throw new SaveCorruptError('This is not a playable life. Laboratory saves cannot be imported into this chronology.');
  if (!('version' in input)) throw new SaveVersionError(undefined);
  if (input.version === 1) return validatePlayableStateV1(input);
  // A version-2 record keeps its duplicated ledger until the load upgrade strips it,
  // so its recorded checksums still verify.
  if (input.version === 2) return validatePlayableStateV2(input);
  if (input.version === 3) return validatePlayableState(input);
  throw new SaveVersionError(input.version);
}

/** The store's own state was parsed by this model when it was set, and is only ever handed out as a copy: a version 3
 * state needs no second full check. (Older versions never survive loading; checking one still refuses it.) */
const current = (state: StoredPlayableRunState): PlayableRunState => state.version === 3 ? state : validatePlayableState(state);

/** Both values have passed the save schema. Compare JSON trees without building
 * two sorted strings for every field of every combat tick. Object order is not
 * meaning; array order is. Checksums still use the unchanged canonical encoder. */
function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  if (Array.isArray(left)) return Array.isArray(right) && left.length === right.length && left.every((value, i) => sameValue(value, right[i]));
  if (Array.isArray(right)) return false;
  const a = left as Record<string, unknown>, b = right as Record<string, unknown>;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every(key => Object.hasOwn(b, key) && sameValue(a[key], b[key]));
}

type PlayOperation = { kind: 'play'; accepted: AcceptedRunIntent; intentDigest: string; changes: ReturnType<typeof patchSchema.parse> };
const patchSchema = PlayableRunStateSchema.partial();
const model: RollingModel<StoredPlayableRunState, PlayOperation> = {
  parse: readStoredPlayableSave,
  // A version-3 digest covers the stripped bytes every version-3 state persists as.
  hash: state => state.version === 3 ? playableHash(stripStoredLedger(state)) : playableHash(state),
  strip: stripStoredLedger,
  parseOperation(input): PlayOperation {
    if (!input || typeof input !== 'object' || !('kind' in input) || input.kind !== 'play' || !('accepted' in input) || !('changes' in input)) throw new SaveCorruptError('Unreadable playable journal operation');
    const accepted = AcceptedRunIntentSchema.parse(input.accepted);
    if (!('intentDigest' in input) || input.intentDigest !== hash(accepted)) throw new SaveCorruptError('The accepted intention failed its checksum');
    return { kind: 'play', accepted, intentDigest: input.intentDigest, changes: patchSchema.parse(input.changes) };
  },
  apply(before, operation) {
    const { accepted } = operation;
    if (accepted.sequence !== before.lastSequence + 1 || accepted.tick !== before.tick || accepted.runOrdinal !== before.envelope.runOrdinal) throw new SaveCorruptError('An accepted intention does not follow this life');
    const after = readStoredPlayableSave({ ...before, ...operation.changes });
    assertPlayableTransition(before, after, accepted.command);
    if (after.lastSequence !== (accepted.command.type === 'new-life' ? -1 : accepted.sequence)) throw new SaveCorruptError('The resulting life has the wrong intention sequence');
    return after;
  },
  upgrade: state => state.version === 3 ? state : readPlayableSave(state),
};

export type RunStoreOptions = FreshLifeOptions & Readonly<{
  initialize: LifeInitializer;
  /** Continues a life saved under another content bundle in this one. It may change how the story is held, never the life lived. */
  migrateContent?: (state: PlayableRunState) => PlayableRunState;
  driver?: StorageDriver;
  holderId?: string;
  snapshotEvery?: number;
  leaseTtlMs?: number;
  now?: () => number;
  interrupt?: NonNullable<DurableStoreOptions['interrupt']>;
  /** A host may drive heartbeat itself; browser hosts renew automatically by default. */
  autoHeartbeat?: boolean;
  /** Called once, before that wait begins, so a host can say the previous window is letting go. */
  onLeaseWait?: (wait: LeaseWait) => void;
  decide?: Parameters<typeof reducePlayableIntent>[2];
}>;

export class RunStore {
  readonly #store: DurableRunStore<StoredPlayableRunState, PlayOperation>;
  readonly #options: RunStoreOptions;
  #heartbeat: ReturnType<typeof setInterval> | undefined;
  #heartbeatError: Error | null = null;

  constructor(store: DurableRunStore<StoredPlayableRunState, PlayOperation>, options: RunStoreOptions) {
    this.#store = store;
    this.#options = options;
    if (options.autoHeartbeat !== false) {
      this.#heartbeat = setInterval(() => { void this.heartbeat().catch(error => { this.#heartbeatError = error instanceof Error ? error : new Error(String(error)); }); }, Math.max(1, Math.floor((options.leaseTtlMs ?? 15_000) / 3)));
      this.#heartbeat.unref?.();
    }
  }

  get state(): PlayableRunState { return current(this.#store.state); }
  get paused(): Error | null { return this.#store.paused ?? this.#heartbeatError; }

  /** The reducer receives a detached copy. Show its outcome only when this promise resolves. */
  commit(raw: RunIntent, reduce?: (state: PlayableRunState) => PlayableRunState | Promise<PlayableRunState>): Promise<PlayableRunState> {
    const command = RunIntentSchema.parse(raw);
    if (reduce && command.type !== 'interaction') return Promise.reject(new Error('Custom reducers are only accepted for interactions; built-in intentions keep their own consequences'));
    if (this.#heartbeatError && !this.#store.paused) return Promise.reject(this.#heartbeatError);
    // Each step below checks the whole save; the page draws between them (nextTask), so a fight's tick never stalls a frame.
    return this.#store.commit(async stored => {
      const before = current(stored);
      const accepted: AcceptedRunIntent = { tick: before.tick, sequence: before.lastSequence + 1, runOrdinal: before.envelope.runOrdinal, command };
      await nextTask();
      let next = reduce ? await reduce(structuredClone(before))
        : command.type === 'close-life' ? closePlayableLife(before, command.bequestId)
        : command.type === 'new-life' ? nextPlayableLife(before, command, this.#options.initialize)
        : reducePlayableIntent(before, command, this.#options.decide);
      await nextTask();
      next = reconcilePlayableState(next);
      next.lastSequence = command.type === 'new-life' ? -1 : accepted.sequence;
      // model.apply checks the actual journal patch's transition before any write.
      await nextTask();
      const changes: ReturnType<typeof patchSchema.parse> = {};
      for (const key of Object.keys(next) as (keyof PlayableRunState)[]) {
        if (!sameValue(before[key], next[key])) Object.assign(changes, { [key]: next[key] });
      }
      // The journal persists without the duplicated tactical ledger; replay re-attaches it.
      return { kind: 'play', accepted, intentDigest: hash(accepted), changes: stripStoredLedger(changes) };
    }, command.type === 'death' ? 'death' : 'intent', command.type === 'death' || command.type === 'close-life' || command.type === 'new-life').then(current);
  }

  closeLife(bequestId: string | null = null): Promise<PlayableRunState> {
    return this.commit({ type: 'close-life', bequestId });
  }

  newLife(options: LifeOptions): Promise<PlayableRunState> {
    return this.commit({ type: 'new-life', ...options });
  }

  async retry(): Promise<void> { await this.#store.retry(); this.#heartbeatError = null; }
  heartbeat(): Promise<void> { return this.#store.heartbeat(); }
  checkpoint(): Promise<void> { return this.#store.rotateCheckpoint(); }
  async close(): Promise<void> {
    clearInterval(this.#heartbeat);
    await this.#store.close();
  }
}

/** A life saved under an earlier content bundle is upgraded on load, once, and checkpointed under this bundle.
 * The migration may only change how the story is held: a changed ledger, life or opening keeps the save untouched. */
function withContentMigration({ contentManifestHash: bundle, migrateContent }: RunStoreOptions): typeof model {
  if (bundle === undefined || !migrateContent) return model;
  return { ...model, upgrade: stored => {
    const state = readStoredPlayableSave(model.upgrade!(stored)) as PlayableRunState;
    if (state.envelope.contentManifestHash === bundle) return state;
    const migrated = { ...migrateContent(structuredClone(state)), envelope: { ...state.envelope, contentManifestHash: bundle } };
    for (const key of ['frozenContinuity', 'continuity', 'pcId', 'closure'] as const) {
      if (canonical(migrated[key]) !== canonical(state[key])) throw new SaveCorruptError(`A content migration changed ${key}; the original save has been kept`);
    }
    return migrated;
  } };
}

/** Continue if present; otherwise establish one fresh chronology. Never overwrite on load failure. */
export async function createRunStore(options: RunStoreOptions): Promise<RunStore> {
  const namespace = options.namespace ?? 'production';
  const driver = options.driver ?? IndexedDbDriver.fromGlobal(`${namespace}:${options.chronologyId}`);
  const store = await DurableRunStore.open({
    ...options, namespace, driver, model: withContentMigration(options), holderId: options.holderId ?? crypto.randomUUID(),
  });
  try {
    const state = await store.load(() => createPlayableRun(options, options.initialize));
    if (options.contentManifestHash !== undefined && state.envelope.contentManifestHash !== options.contentManifestHash) {
      throw new SaveCorruptError('This life needs a different content bundle. Its save has been kept; use the matching build or a supported migration before continuing.');
    }
    return new RunStore(store, options);
  } catch (error) {
    // A full disk may also refuse lease release; preserve the actionable load error.
    try { await store.close(); } catch { /* connection still closes; the lease expires */ }
    throw error;
  }
}

export const runDatabaseName = (namespace: string, chronologyId: string): string => databaseName(`${namespace}:${chronologyId}`);
export { activeScene };
